import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CheckCircle2 } from 'lucide-react';

interface Activation {
  id: string;
  licenseKey: string;
  systemSignature: string;
  platform: string;
  status: string;
  activatedAt: number;
  lastCheckIn?: number;
}

function Activations() {
  const [activations, setActivations] = useState<Activation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchActivations();
  }, []);

  const fetchActivations = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/activations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setActivations(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch activations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/activations/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchActivations();
      }
    } catch (error) {
      console.error('Failed to revoke activation:', error);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      ACTIVE: 'bg-green-500',
      REVOKED: 'bg-red-500',
      EXPIRED: 'bg-gray-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CheckCircle2 className="h-8 w-8" />
            Activations
          </h2>
          <p className="text-muted-foreground mt-2">
            License activation history and management
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Activations</CardTitle>
          <CardDescription>
            {activations.length} activation{activations.length !== 1 ? 's' : ''} recorded
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>License Key</TableHead>
                <TableHead>System Signature</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Activated</TableHead>
                <TableHead>Last Check-in</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No activations found
                  </TableCell>
                </TableRow>
              ) : (
                activations.map((activation) => (
                  <TableRow key={activation.id}>
                    <TableCell className="font-mono text-xs max-w-xs truncate">
                      {activation.licenseKey}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-xs truncate">
                      {activation.systemSignature}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{activation.platform}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeColor(activation.status)}>
                        {activation.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(activation.activatedAt * 1000).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {activation.lastCheckIn
                        ? new Date(activation.lastCheckIn * 1000).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      {activation.status === 'ACTIVE' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevoke(activation.id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default Activations;
